import { useFormState } from "common/hooks";
import { FetchError, AllFieldErrors } from "common/fetch/types";
import { Optional } from "common/utils/types";
import { MONTH_SHORT_NAMES } from "common/constants/dates";
import { useClinicGroupCountry } from "common/contexts/UIContext";
import { CountryCode } from "common/constants/address";

import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import TextInput from "client/common/components/inputs/TextInput";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import Select from "client/common/components/inputs/Select";
import StateSelect from "client/common/components/inputs/StateSelect";
import Form from "client/common/components/forms/Form";
import Tooltip from "client/common/components/content/Tooltip";

import styles from "../../styles/ControlledSubstancesForm.module.less";

type FormValues = {
  drivers_license_number: string;
  drivers_license_state: string;
  birth_year: string;
  birth_month: string;
  birth_day: string;
};

type ControlledSubstancesFormProps = {
  initialValues: FormValues;
  onSubmit: (values: FormValues) => void;
  isLoading: boolean;
  error: Optional<FetchError>;
  fieldErrors: AllFieldErrors<FormValues>;
};

const getUIString = (field: string, clinicGroupCountry: CountryCode = CountryCode.US) => {
  switch (field) {
    case "drivers_license_number":
      return "Driver's license number";
    case "drivers_license_state":
      return `Driver's license ${clinicGroupCountry === CountryCode.CA ? "province" : "state"}`;
    case "birth_month":
      return "Date of birth";
    case "birth_day":
      return "Date of birth";
    case "birth_year":
      return "Date of birth";
    default:
      return "";
  }
};

const ControlledSubstancesForm = ({
  initialValues,
  onSubmit,
  isLoading,
  error,
  fieldErrors,
}: ControlledSubstancesFormProps) => {
  const [values, updateField] = useFormState(initialValues);
  const clinicGroupCountry = useClinicGroupCountry();

  // users can't delete a field previously supplied b/c this could cause issues for existing orders (backend serializers also validates this)
  const missingFields = Object.keys(values).filter(
    (k) => !values[k as keyof FormValues] && initialValues[k as keyof FormValues],
  );

  const canSubmit = !missingFields.length;

  const renderMissingRequiredFieldsTooltipContent = () => {
    const missingFieldsUIString = missingFields.map((field) =>
      getUIString(field, clinicGroupCountry),
    );
    const filteredMissingFields = missingFieldsUIString.filter(
      (item, index) => missingFieldsUIString.indexOf(item) === index,
    );
    return (
      <div className={styles.tooltip}>
        Please complete the following fields before proceeding:
        <ul className={styles.tooltipBullet}>
          {filteredMissingFields.map((field) => (
            <li key={field}>{field}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <Form className={styles.form} canSubmit={canSubmit} onSubmit={() => onSubmit(values)}>
      <div className={styles.formContainer}>
        {error && (
          <DrawerLayout.Alerts>
            <FetchErrorAlert error={error} />
          </DrawerLayout.Alerts>
        )}
        <DrawerLayout.Form.Group>
          <TextInput
            value={values.drivers_license_number}
            label={getUIString("drivers_license_number")}
            onChange={updateField("drivers_license_number")}
            errors={fieldErrors.drivers_license_number}
          />
        </DrawerLayout.Form.Group>
        <DrawerLayout.Form.Group>
          <StateSelect
            label={getUIString("drivers_license_state", clinicGroupCountry)}
            value={values.drivers_license_state}
            onChange={updateField("drivers_license_state")}
            errors={fieldErrors.drivers_license_state}
          />
        </DrawerLayout.Form.Group>
        <DrawerLayout.Form.Group align="horizontal">
          <Select
            label="Birth month"
            value={values.birth_month}
            onChange={updateField("birth_month")}
            className={styles.monthInput}
            errors={fieldErrors.birth_month}
          >
            {MONTH_SHORT_NAMES.map((monthName, index) => (
              <Select.Option value={`${index + 1}`} key={monthName}>
                {monthName}
              </Select.Option>
            ))}
          </Select>
          <TextInput
            type="number"
            label="Birth day"
            value={values.birth_day}
            maxLength={2}
            onChange={updateField("birth_day")}
            errors={fieldErrors.birth_day}
          />
          <TextInput
            type="number"
            label="Birth year"
            value={values.birth_year}
            maxLength={4}
            onChange={updateField("birth_year")}
            errors={fieldErrors.birth_year}
          />
        </DrawerLayout.Form.Group>
      </div>
      <Tooltip
        isEnabled={!canSubmit}
        content={renderMissingRequiredFieldsTooltipContent()}
        className={styles.submitAction}
      >
        <DrawerLayout.Actions.Button
          disabled={!canSubmit || isLoading}
          type="submit"
          isLoading={isLoading}
        >
          Update
        </DrawerLayout.Actions.Button>
      </Tooltip>
    </Form>
  );
};

export default ControlledSubstancesForm;
